import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { API_CONFIG } from '@/config/api';
import { getPageDataPollingInterval, startPageDataRefresh } from '@/utils/pageDataRefresh';
import { PURCHASE_PAYMENT_INVALIDATED } from '@/utils/purchasePaymentInvalidation';

/** Events only invalidate authenticated reads; a missed event is caught by the timer. */
export function usePurchasePaymentUpdates(refresh: () => unknown | Promise<unknown>, enabled = true) {
  const latest = useRef(refresh);
  latest.current = refresh;
  useEffect(() => {
    if (!enabled) return;
    const automatic = getPageDataPollingInterval() !== false;
    const token = localStorage.getItem('token');
    if (!token) return;
    let disposed = false;
    let running = false;
    let pending = false;
    let invalidated = false;
    let lastRefresh = Date.now();
    let debounce: ReturnType<typeof setTimeout> | undefined;
    const valid = () => !disposed && token === localStorage.getItem('token') && !document.hidden && navigator.onLine;
    const run = async () => {
      if (!valid()) return;
      if (running) { pending = true; return; }
      running = true;
      invalidated = false;
      try { await latest.current(); lastRefresh = Date.now(); } catch { invalidated = true; /* Existing API error handling owns errors. */ }
      finally {
        running = false;
        if (pending && valid()) { pending = false; schedule(); }
      }
    };
    const schedule = () => {
      if (!valid()) return;
      // A continuous stream of updates must not keep postponing the refresh.
      if (debounce) return;
      debounce = setTimeout(() => { debounce = undefined; void run(); }, 500);
    };
    const base = process.env.REACT_APP_WS_URL || API_CONFIG.getBaseURL() || window.location.origin;
    const socket = automatic ? io(`${base.replace(/\/$/, '')}/purchase-payment-updates`, {
      auth: { token }, transports: ['websocket'], reconnection: true, reconnectionDelayMax: 30000,
    }) : undefined;
    socket?.on('payment-updated', schedule);
    socket?.on('connect', schedule);
    const timer = automatic ? startPageDataRefresh(() => {
      if (!socket?.connected || Date.now() - lastRefresh >= 300000) schedule();
    }) : undefined;
    const stop = () => { disposed = true; socket?.disconnect(); };
    const invalidate = () => { invalidated = true; schedule(); };
    // Local pages stay quiet except after explicit mutations, including reviews
    // in another tab. Hidden pages read fresh state when the user returns.
    const resume = () => { if (automatic || invalidated) schedule(); };
    const storage = (event: StorageEvent) => {
      if (event.key === 'token' && event.newValue !== token) stop();
      else if (event.key === PURCHASE_PAYMENT_INVALIDATED && event.newValue && event.newValue !== event.oldValue) invalidate();
    };
    window.addEventListener('storage', storage);
    window.addEventListener('admin-auth-cleared', stop);
    window.addEventListener(PURCHASE_PAYMENT_INVALIDATED, invalidate);
    window.addEventListener('focus', resume);
    window.addEventListener('online', resume);
    document.addEventListener('visibilitychange', resume);
    return () => {
      stop();
      socket?.removeAllListeners();
      if (timer !== undefined) window.clearInterval(timer);
      if (debounce) clearTimeout(debounce);
      window.removeEventListener('storage', storage);
      window.removeEventListener('admin-auth-cleared', stop);
      window.removeEventListener(PURCHASE_PAYMENT_INVALIDATED, invalidate);
      window.removeEventListener('focus', resume);
      window.removeEventListener('online', resume);
      document.removeEventListener('visibilitychange', resume);
    };
  }, [enabled]);
}
